import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const VerifyEmailPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { verifyEmail } = useAuth();
  
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'already-verified'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      setMessage('Invalid verification link. Please check your email for the correct link.');
      return;
    }

    const handleVerification = async () => {
      try {
        console.log('🔄 Frontend: Verifying email with token:', token);
        const result = await verifyEmail(token);
        
        if (result.success) {
          if (result.alreadyVerified) {
            setStatus('already-verified');
            setMessage('Your email is already verified. You can sign in to your account.');
          } else {
            setStatus('success');
            setMessage('Email verified successfully! You can now sign in to your account.');
          }
        } else {
          setStatus('error');
          setMessage(result.error || 'Email verification failed. Please try again.');
        }
      } catch (error) {
        console.error('❌ Email verification error:', error);
        setStatus('error');
        setMessage('An unexpected error occurred. Please try again.');
      }
    };

    handleVerification();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]); // Removed verifyEmail from dependency array to prevent double verification

  const getStatusIcon = () => {
    switch (status) {
      case 'loading':
        return '⏳';
      case 'success':
      case 'already-verified':
        return '✅';
      case 'error':
        return '❌';
      default:
        return '⏳';
    }
  };

  const getStatusTitle = () => {
    switch (status) {
      case 'loading':
        return 'Verifying Your Email...';
      case 'success':
        return 'Email Verified!';
      case 'already-verified':
        return 'Already Verified';
      case 'error':
        return 'Verification Failed';
      default:
        return 'Verifying Your Email...';
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'loading':
        return 'text-blue-400';
      case 'success':
      case 'already-verified':
        return 'text-green-400';
      case 'error':
        return 'text-red-400';
      default:
        return 'text-blue-400';
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
      <div className="max-w-md w-full mx-auto px-6">
        <div className="bg-gray-800 rounded-lg p-8 text-center">
          <div className="mb-6">
            <div className="text-6xl mb-4">{getStatusIcon()}</div>
            <h1 className={`text-2xl font-bold mb-2 ${getStatusColor()}`}>
              {getStatusTitle()}
            </h1>
            <p className="text-gray-400">
              {message}
            </p>
          </div>
          
          <div className="space-y-4">
            {status === 'loading' && (
              <div className="flex items-center justify-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-400"></div>
                <span className="text-sm text-gray-400">Please wait...</span>
              </div>
            )}
            
            {(status === 'success' || status === 'already-verified') && (
              <div className="space-y-4">
                <div className="p-4 bg-green-900/50 border border-green-500 rounded-md">
                  <p className="text-sm text-green-400">
                    You can now sign in with your email and password.
                  </p>
                </div>
                
                <Link
                  to="/login"
                  className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-md transition-colors duration-200"
                >
                  Go to Sign In
                </Link>
              </div>
            )}
            
            {status === 'error' && (
              <div className="space-y-4">
                <div className="p-4 bg-red-900/50 border border-red-500 rounded-md">
                  <p className="text-sm text-red-400">
                    {message}
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Link
                    to="/login"
                    className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-md transition-colors duration-200 mr-4"
                  >
                    Back to Sign In
                  </Link>
                  
                  <p className="text-sm text-gray-400">
                    Need help? Contact support or try requesting a new verification email.
                  </p>
                </div>
              </div>
            )}
          </div>
          
          <div className="mt-8 pt-4 border-t border-gray-700">
            <Link 
              to="/" 
              className="text-sm text-gray-400 hover:text-gray-300"
            >
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage; 